import { useCallback, useEffect, useRef, useState } from 'react';

import { ChevronDown } from 'lucide-react';

import { IconButton, IconButtonProps } from '@/components/core';
import { cn } from '@/lib/utils';
import { useChats } from '@/store/chats';

export type ScrollButtonProps = Omit<IconButtonProps, 'icon' | 'aria-label'> & {
  containerRef: React.RefObject<HTMLElement | null>;
  className?: string;
  threshold?: number;
};

function ScrollButton({
  containerRef,
  className,
  threshold = 100,
  variant = 'outline',
  size = 'sm',
  ...props
}: ScrollButtonProps) {
  const [isVisible, setIsVisible] = useState(false);
  const documentChatData = useChats(state => state.chatState.document);
  const jobChatData = useChats(state => state.chatState.job);
  const animationFrameIdRef = useRef<number | null>(null);
  const lastVisibleStateRef = useRef(false);

  useEffect(() => {
    const isNewJobSession = jobChatData.isNewSession;
    const isNewDocSession = documentChatData.isNewSession;

    if (isNewJobSession || isNewDocSession) {
      setIsVisible(false);
      lastVisibleStateRef.current = false;
    }
  }, [jobChatData, documentChatData]);

  const handleScroll = useCallback(() => {
    if (animationFrameIdRef.current !== null) {
      return;
    }

    animationFrameIdRef.current = requestAnimationFrame(() => {
      if (containerRef.current) {
        const { scrollTop, scrollHeight, clientHeight } = containerRef.current;
        const shouldBeVisible =
          scrollTop + clientHeight < scrollHeight - threshold;

        // only update state if the value changed
        if (shouldBeVisible !== lastVisibleStateRef.current) {
          lastVisibleStateRef.current = shouldBeVisible;
          setIsVisible(shouldBeVisible);
        }
      }
      animationFrameIdRef.current = null;
    });
  }, [containerRef, threshold]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    container.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();

    return () => {
      container.removeEventListener('scroll', handleScroll);
      if (animationFrameIdRef.current !== null) {
        cancelAnimationFrame(animationFrameIdRef.current);
        animationFrameIdRef.current = null;
      }
    };
  }, [containerRef, handleScroll]);

  const handleScrollToBottom = useCallback(() => {
    if (containerRef.current) {
      containerRef.current.scrollTo({
        top: containerRef.current.scrollHeight,
        behavior: 'smooth',
      });
    }
  }, [containerRef]);

  return (
    <IconButton
      icon={<ChevronDown className='h-4 w-4' />}
      aria-label='Scroll to bottom'
      variant={variant}
      size={size}
      className={cn(
        'h-8 w-8 rounded-full transition-all duration-150 ease-out',
        isVisible
          ? 'translate-y-0 scale-100 opacity-100'
          : 'pointer-events-none translate-y-4 scale-95 opacity-0',
        className
      )}
      onClick={handleScrollToBottom}
      {...props}
    />
  );
}

export { ScrollButton };
